import { Link, router } from "@inertiajs/react";
import { useState } from "react";
import { Archive, CheckCircle2, Circle, ExternalLink, Loader2, Wand2 } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Submission, SUBMISSION_STATUS_BADGE, fmt } from "./_types";

interface Props {
  candidateId: number;
  submissions: Submission[];
  aiAssist: boolean;
}

export default function SubmissionsTab({ candidateId, submissions, aiAssist }: Props) {
  const [generatingAll, setGeneratingAll] = useState(false);
  const [togglingId, setTogglingId]       = useState<number | null>(null);

  const draftedWithJob = submissions.filter((s) => s.status === "drafted" && s.job_id);
  const canGenerateAll = aiAssist && draftedWithJob.length > 0;

  function generateAll() {
    setGeneratingAll(true);
    router.post(`/candidates/${candidateId}/generate_all_cover_letters`, {}, {
      onFinish: () => setGeneratingAll(false),
    });
  }

  function toggleApprove(submissionId: number) {
    setTogglingId(submissionId);
    router.patch(`/submissions/${submissionId}/approve`, {}, {
      onFinish: () => setTogglingId(null),
    });
  }

  return (
    <div className="space-y-3">
      {submissions.length > 0 && (
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-gray-400">{submissions.length} submission{submissions.length !== 1 ? "s" : ""}</p>
          <div className="flex items-center gap-2">
            {canGenerateAll && (
              <button
                onClick={generateAll}
                disabled={generatingAll}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingAll
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Queuing...</>
                  : <><Wand2 className="w-3 h-3" /> Generate All</>
                }
              </button>
            )}
            <a
              href={`/candidates/${candidateId}/export_zip`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-700 transition-colors"
            >
              <Archive className="w-3 h-3" /> Download ZIP
            </a>
          </div>
        </div>
      )}

      {submissions.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-16">No submissions yet.</p>
      ) : (
        submissions.map((s) => (
          <Card key={s.id}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link href={`/submissions/${s.id}`}
                      className="text-sm font-medium text-gray-900 hover:text-green-700">
                      {s.firm_name}
                    </Link>
                    {s.job_id && s.job_title && (
                      <Link href={`/job-alerts/${s.job_id}`}
                        className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5">
                        {s.job_title} <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                    {s.cover_letter_generated && (
                      <span className="text-xs text-purple-600 inline-flex items-center gap-0.5">
                        <Wand2 className="w-3 h-3" /> AI letter
                      </span>
                    )}
                  </div>
                  {s.firm_contact_name && (
                    <p className="text-xs text-gray-500 mt-0.5">Contact: {s.firm_contact_name}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                    <span>{s.submission_method === "portal_plus_email" ? "Portal + Email" : "Email only"}</span>
                    <span>Created {fmt(s.created_at)}</span>
                    {s.submitted_at && <span>Submitted {fmt(s.submitted_at)}</span>}
                  </div>
                  {s.notes && <p className="text-xs text-gray-500 mt-1.5 italic">{s.notes}</p>}
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${SUBMISSION_STATUS_BADGE[s.status] ?? "bg-gray-100 text-gray-600"}`}>
                    {s.status.replace(/_/g, " ")}
                  </span>
                  <button
                    onClick={() => toggleApprove(s.id)}
                    disabled={togglingId === s.id}
                    title={s.candidate_approved ? "Remove approval" : "Mark as approved"}
                    className={`inline-flex items-center gap-1 text-xs transition-colors ${
                      s.candidate_approved
                        ? "text-green-700 hover:text-green-900"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {togglingId === s.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : s.candidate_approved
                        ? <><CheckCircle2 className="w-3.5 h-3.5" /> Approved</>
                        : <><Circle className="w-3.5 h-3.5" /> Approve</>
                    }
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
